import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { AlertTriangle, FileText, RefreshCw, Sparkles, Database, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CountUp } from './CountUp';
import { containerVariants, itemVariants } from './motion';

interface KnowledgeSummaryStripProps {
  productsInKnowledgeBase: number;
  faqCount: number;
  missingDescriptions: number;
  lastReindexAt: string | null;
  onReindex: () => Promise<unknown>;
}

// 5-bar mini histogram equalizer
function MiniSparkBars({ tone = 'primary' }: { tone?: 'primary' | 'success' | 'warning' | 'indigo' }) {
  const heights = [40, 70, 55, 90, 75];
  const colorMap = {
    primary: 'bg-emerald-500/70',
    success: 'bg-emerald-500/80',
    warning: 'bg-amber-500/80',
    indigo: 'bg-indigo-500/80',
  };

  return (
    <div className="flex items-end gap-0.5 h-3.5 px-1 py-0.5 rounded bg-slate-100/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 shrink-0">
      {heights.map((h, i) => (
        <span
          key={i}
          className={`w-0.5 rounded-full ${colorMap[tone]}`}
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}

export function KnowledgeSummaryStrip({
  productsInKnowledgeBase,
  faqCount,
  missingDescriptions,
  lastReindexAt,
  onReindex,
}: KnowledgeSummaryStripProps) {
  const [reindexing, setReindexing] = useState(false);
  const [lastReindexed, setLastReindexed] = useState<string | null>(lastReindexAt);

  const handleReindex = async () => {
    if (reindexing) return;
    setReindexing(true);
    try {
      await onReindex();
      setLastReindexed(new Date().toISOString());
      toast.success('AI knowledge re-indexed');
    } catch {
      toast.error('Re-indexing failed. Try again in a moment.');
    } finally {
      setReindexing(false);
    }
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
              <Database className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              AI Knowledge Telemetry
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              RAG Active
            </span>
          </div>

          <Button
            onClick={handleReindex}
            disabled={reindexing}
            size="sm"
            className="h-8 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs gap-1.5 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${reindexing ? 'animate-spin' : ''}`} />
            {reindexing ? 'Re-indexing Embeddings…' : 'Re-index AI Knowledge'}
          </Button>
        </div>

        {/* 4 Telemetry Metric Capsules */}
        <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 lg:grid-cols-4 bg-white/70 dark:bg-slate-950/40">
          {/* Metric 1 */}
          <motion.div variants={itemVariants} className="w-full">
            <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/50 hover:shadow-xs cursor-pointer w-full">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 transition-all duration-200 group-hover/tile:bg-primary/10 dark:group-hover/tile:bg-primary/20 group-hover/tile:text-primary group-hover/tile:scale-105">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
                    <CountUp value={productsInKnowledgeBase} />
                  </span>
                  <MiniSparkBars tone="success" />
                </div>
                <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
                  IN AI KNOWLEDGE
                </span>
              </div>
            </div>
          </motion.div>

          {/* Metric 2 */}
          <motion.div variants={itemVariants} className="w-full">
            <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/50 hover:shadow-xs cursor-pointer w-full">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 transition-all duration-200 group-hover/tile:bg-primary/10 dark:group-hover/tile:bg-primary/20 group-hover/tile:text-primary group-hover/tile:scale-105">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
                    <CountUp value={faqCount} />
                  </span>
                  <MiniSparkBars tone="indigo" />
                </div>
                <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
                  FAQ & POLICIES
                </span>
              </div>
            </div>
          </motion.div>

          {/* Metric 3 */}
          <motion.div variants={itemVariants} className="w-full">
            <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/50 hover:shadow-xs cursor-pointer w-full">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 transition-all duration-200 group-hover/tile:bg-primary/10 dark:group-hover/tile:bg-primary/20 group-hover/tile:text-primary group-hover/tile:scale-105">
                <RefreshCw className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-base font-bold tracking-tight text-slate-900 dark:text-white truncate leading-tight">
                    {lastReindexed ? formatDistanceToNow(new Date(lastReindexed), { addSuffix: true }) : 'Real-time'}
                  </span>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                </div>
                <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
                  LAST RE-INDEX
                </span>
              </div>
            </div>
          </motion.div>

          {/* Metric 4 */}
          <motion.div variants={itemVariants} className="w-full">
            <div className={`group/tile relative flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xs cursor-pointer w-full ${
              missingDescriptions > 0
                ? 'border-amber-300/80 dark:border-amber-800/80 bg-amber-50/30 dark:bg-amber-950/30 hover:border-amber-400 dark:hover:border-amber-600'
                : 'border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/40 dark:hover:border-primary/50'
            }`}>
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200 group-hover/tile:scale-105 ${
                missingDescriptions > 0 ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' : 'bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 group-hover/tile:bg-primary/10 dark:group-hover/tile:bg-primary/20 group-hover/tile:text-primary'
              }`}>
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
                    {missingDescriptions}
                  </span>
                  {missingDescriptions > 0 ? (
                    <span className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.2 text-[8px] font-bold text-white uppercase">
                      Action
                    </span>
                  ) : (
                    <MiniSparkBars tone="success" />
                  )}
                </div>
                <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
                  NEEDS ATTENTION
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </Card>
    </motion.div>
  );
}

export default KnowledgeSummaryStrip;
